// AdminPromotions.jsx — backlog #1 (built 2026-07-10). Admin sets a promo
// price, optional case limit, and optional end date per product. Promos
// auto-deactivate when the date passes or the limit sells out (computed via
// utils/promo.js — no cron needed). The catalog shows base price struck
// through + promo price in red; the order transaction is the price authority
// and counts promo cases sold. QB gets the discounted price on the invoice
// LINE (priceAtOrder) — the QB item base price is never touched.
import { useEffect, useState } from "react";
import { db } from "../config/firebase";
import { doc, updateDoc, deleteField } from "firebase/firestore";
import { isPromoActive, promoRemaining } from "../utils/promo";
import { getAllProducts, invalidateProductCache } from "../services/dataCache";

function fmtDate(ts) {
  if (!ts) return "no end date";
  const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "no end date";
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

export default function AdminPromotions() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  // create-promo form
  const [search, setSearch] = useState("");
  const [selectedSku, setSelectedSku] = useState("");
  const [price, setPrice] = useState("");
  const [qtyLimit, setQtyLimit] = useState("");
  const [endDate, setEndDate] = useState("");
  const [productExpiry, setProductExpiry] = useState(""); // product's own best-before date
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      const all = await getAllProducts(); // session cache — 0 reads on revisit
      setProducts([...all].sort((a, b) => (a.sku || "").localeCompare(b.sku || "")));
      setLoading(false);
    }
    load();
  }, []);

  function popFlash(msg) {
    setFlash(msg);
    setTimeout(() => setFlash(""), 2500);
  }

  const withPromo = products.filter(p => p.promoPrice != null);
  const searchResults = search
    ? products.filter(p =>
        (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.sku || "").includes(search))
    : [];
  const selectedProduct = products.find(p => p.sku === selectedSku) || null;

  async function handleCreate(e) {
    e.preventDefault();
    setError("");
    if (!selectedProduct) { setError("Pick a product first."); return; }
    const promoPrice = parseFloat(price);
    if (isNaN(promoPrice) || promoPrice <= 0) { setError("Enter a valid promo price."); return; }
    if (promoPrice >= selectedProduct.basePrice) {
      setError(`Promo price must be BELOW the base price ($${selectedProduct.basePrice.toFixed(2)}).`);
      return;
    }
    const limit = qtyLimit.trim() === "" ? null : parseInt(qtyLimit, 10);
    if (limit != null && (isNaN(limit) || limit <= 0)) { setError("Case limit must be a positive number (or blank for no limit)."); return; }

    // Fat-finger guard (Brian, 2026-07-10): discounts deeper than 35% need an
    // explicit yes — catches typos like $2.70 instead of $27.00.
    const discountPct = ((selectedProduct.basePrice - promoPrice) / selectedProduct.basePrice) * 100;
    if (discountPct > 35) {
      const ok = window.confirm(
        `This promo is ${discountPct.toFixed(0)}% OFF the base price ` +
        `($${selectedProduct.basePrice.toFixed(2)} → $${promoPrice.toFixed(2)}).\n\n` +
        `That's a big discount — do you really want to continue?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const fields = {
        promoPrice,
        promoQtyLimit: limit,
        promoSoldQty: 0,
        promoEndsAt: endDate ? new Date(`${endDate}T23:59:59`) : null,
        promoProductExpiry: productExpiry ? new Date(`${productExpiry}T23:59:59`) : null
      };
      await updateDoc(doc(db, "products", selectedProduct.sku), fields);
      invalidateProductCache();
      setProducts(prev => prev.map(p => p.sku === selectedProduct.sku ? { ...p, ...fields } : p));
      popFlash(`Promotion set on ${selectedProduct.name}`);
      setSelectedSku(""); setSearch(""); setPrice(""); setQtyLimit(""); setEndDate(""); setProductExpiry("");
    } catch (err) {
      setError(`Could not save promotion: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleEnd(p) {
    if (!window.confirm(`End the promotion on ${p.name}? The price returns to $${p.basePrice.toFixed(2)}.`)) return;
    try {
      await updateDoc(doc(db, "products", p.sku), {
        promoPrice: deleteField(),
        promoQtyLimit: deleteField(),
        promoSoldQty: deleteField(),
        promoEndsAt: deleteField(),
        promoProductExpiry: deleteField()
      });
      invalidateProductCache();
      setProducts(prev => prev.map(x => x.sku === p.sku
        ? { ...x, promoPrice: undefined, promoQtyLimit: undefined, promoSoldQty: undefined, promoEndsAt: undefined, promoProductExpiry: undefined }
        : x));
      popFlash(`Promotion ended on ${p.name}`);
    } catch (err) {
      setError(`Could not end promotion: ${err.message}`);
    }
  }

  if (loading) return <p>Loading products...</p>;

  return (
    <div className="admin-promotions">
      <h2>Promotions</h2>
      <p className="inventory-help">
        Set a discounted price on a product, optionally capped at a number of cases
        and/or an end date. The promo turns itself off when the date passes or the
        cases sell out — the price then returns to normal automatically. Customers
        see the original price struck through with the promo price in red.
      </p>

      <form className="promo-form" onSubmit={handleCreate}>
        <h3>New promotion</h3>
        {!selectedProduct ? (
          <>
            <input
              placeholder="Search product by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <div className="barcode-match-list">
                {searchResults.slice(0, 8).map(p => (
                  <div key={p.sku} className="barcode-match-row" onClick={() => setSelectedSku(p.sku)}>
                    <span className="sku">{p.sku}</span>
                    <span>{p.name}</span>
                    <span>${p.basePrice.toFixed(2)}</span>
                    {p.promoPrice != null && <span className="already-mapped">has promo</span>}
                  </div>
                ))}
                {searchResults.length === 0 && <p className="pick-empty">No products match.</p>}
              </div>
            )}
          </>
        ) : (
          <div className="promo-selected-product">
            <strong>{selectedProduct.name}</strong> (SKU {selectedProduct.sku} · base ${selectedProduct.basePrice.toFixed(2)})
            <button type="button" className="mini-btn" onClick={() => setSelectedSku("")}>Change</button>
          </div>
        )}

        <div className="promo-fields">
          <label>
            Promo price (CAD)
            <input type="number" step="0.01" min="0.01" placeholder="e.g. 24.50"
              value={price} onChange={(e) => setPrice(e.target.value)} />
          </label>
          <label>
            Case limit (blank = none)
            <input type="number" step="1" min="1" placeholder="e.g. 50"
              value={qtyLimit} onChange={(e) => setQtyLimit(e.target.value)} />
          </label>
          <label>
            Ends on (blank = until ended)
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <label>
            Product expiry / best-before (optional)
            <input type="date" value={productExpiry} onChange={(e) => setProductExpiry(e.target.value)} />
          </label>
        </div>
        <p className="inventory-help-note">
          Product expiry shows customers a red "Short date — best before [date]" note, for moving short-dated stock.
        </p>
        {error && <p className="error">{error}</p>}
        {flash && <p className="inventory-success">{flash}</p>}
        <button type="submit" disabled={saving || !selectedProduct}>
          {saving ? "Saving..." : "Start Promotion"}
        </button>
      </form>

      <h3>Current promotions ({withPromo.length})</h3>
      {withPromo.length === 0 ? (
        <p className="pick-empty">No promotions set.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>SKU</th><th>Product</th><th>Base</th><th>Promo</th>
              <th>Sold / Limit</th><th>Ends</th><th>Product expiry</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {withPromo.map(p => {
              const active = isPromoActive(p);
              const remaining = promoRemaining(p);
              return (
                <tr key={p.sku}>
                  <td>{p.sku}</td>
                  <td>{p.name}</td>
                  <td>${p.basePrice.toFixed(2)}</td>
                  <td className="promo-price">${p.promoPrice.toFixed(2)}</td>
                  <td>
                    {p.promoSoldQty || 0} / {p.promoQtyLimit ?? "∞"}
                    {active && remaining != null && remaining <= 10 && (
                      <span className="promo-low-warning"> ⚠ {remaining} left</span>
                    )}
                  </td>
                  <td>{fmtDate(p.promoEndsAt)}</td>
                  <td>{p.promoProductExpiry ? fmtDate(p.promoProductExpiry) : "—"}</td>
                  <td>
                    <span className={active ? "status-synced" : "status-cancelled"}>
                      {active ? "Active" : "Ended (auto)"}
                    </span>
                  </td>
                  <td>
                    <button className="mini-btn danger" onClick={() => handleEnd(p)}>End</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="inventory-help-note">
        "Ended (auto)" promos are already off for customers — the End button just clears them from this list.
        Note: the final order of a promo may exceed the case limit slightly (the whole order keeps the promo price).
      </p>
    </div>
  );
}
